// Creating an engagement — shared by the Dashboard (pick any client) and the Clients page
// (the client is already known). One component so the two entry points can never drift.
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import * as crm from '../api/crm.js';
import { isLanguage, LANGUAGES, LANGUAGE_LABEL } from '../../shared/language.js';
import { request } from '../api/client.js';
import { useToast } from './Toast.js';
import { useT } from '../i18n/index.js';
import './NewEngagementModal.de.js';
import './NewEngagementModal.fr.js';

/** `t()` for a sentence with markup inside: each `{name}` placeholder is filled with a React node. */
function tx(t: (key: string) => string, key: string, nodes: Record<string, ReactNode>): ReactNode {
  return t(key).split(/(\{\w+\})/g).map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    return m ? <Fragment key={i}>{nodes[m[1]]}</Fragment> : part;
  });
}

export interface NewEngagementModalProps {
  open: boolean;
  onClose: () => void;
  /** Created engagement — the caller decides whether to select it and where to go. */
  onCreated: (engagement: crm.Engagement) => void;
  /** Pre-selected and locked when the modal is opened from a client's own page. */
  clientId?: number;
  clients?: crm.Client[];
  /** Candidate parents; filtered to the chosen client. */
  engagements?: Array<{ id: number; code: string; name: string; client_id: number }>;
}

const EMPTY = {
  client_id: '', name: '', code: '', commercial_model: 'fixed', currency: 'CHF',
  review_window_days: '10', parent_engagement_id: '', kickoff_date: '',
  sponsor_contact_id: '', estimate_value: '', language: '',
};

export default function NewEngagementModal(
  { open, onClose, onCreated, clientId, clients = [], engagements = [] }: NewEngagementModalProps,
) {
  const toast = useToast();
  const t = useT();
  const [form, setForm] = useState({ ...EMPTY, client_id: clientId ? String(clientId) : '' });
  const [contacts, setContacts] = useState<crm.Contact[]>([]);
  const [saving, setSaving] = useState(false);

  const chosenClient = form.client_id ? Number(form.client_id) : null;

  // The three `default_*` switches on the Admin tab are READ HERE, and this is the only place
  // that reads them. Until this existed, the Switches zone was honestly reporting that five of
  // six settings were read by nothing: the literals in EMPTY above decided every new engagement
  // and the stored value was decoration. The DB column defaults stay as the floor, so a failed
  // fetch costs the seeding, never the creation.
  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, client_id: clientId ? String(clientId) : '' });
    let cancelled = false;
    request<Record<string, string>>('/settings')
      .then((s) => {
        if (cancelled) return;
        setForm((f) => ({
          ...f,
          commercial_model: s.default_commercial_model || f.commercial_model,
          currency: s.default_currency || f.currency,
          review_window_days: s.default_review_window_days || f.review_window_days,
        }));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [open, clientId]);

  // The sponsor list follows the chosen client — you can only name their own people.
  useEffect(() => {
    let cancelled = false;
    if (!open || !chosenClient) { setContacts([]); return; }
    crm.getClientContacts(chosenClient)
      .then((rows) => { if (!cancelled) setContacts(rows); })
      .catch(() => { if (!cancelled) setContacts([]); });
    return () => { cancelled = true; };
  }, [open, chosenClient]);

  if (!open) return null;

  const currency = form.currency.trim().toUpperCase() || 'CHF';

  async function create() {
    if (!form.client_id || !form.name.trim()) {
      toast('warn', t('Client and name are required'));
      return;
    }
    setSaving(true);
    try {
      const created = await crm.createEngagement({
        client_id: Number(form.client_id),
        name: form.name.trim(),
        code: form.code.trim() ? form.code.trim().toUpperCase() : undefined,
        commercial_model: form.commercial_model,
        currency,
        review_window_days: Number(form.review_window_days) || 10,
        language: form.language || undefined,   // empty = inherit the client's
        parent_engagement_id: form.parent_engagement_id ? Number(form.parent_engagement_id) : null,
        kickoff_date: form.kickoff_date || null,
        sponsor_contact_id: form.sponsor_contact_id ? Number(form.sponsor_contact_id) : null,
        estimate_value: form.estimate_value.trim() ? Number(form.estimate_value) : null,
      });
      toast('ok', t('Engagement {code} created', { code: created.code }));
      onCreated(created);
      onClose();
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('New engagement')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {clientId ? null : (
            <div className="form-group">
              <label className="form-label">{t('Client')}</label>
              <select
                className="form-select"
                value={form.client_id}
                onChange={(ev) => setForm({ ...form, client_id: ev.target.value, sponsor_contact_id: '' })}
              >
                <option value="">{t('select a client…')}</option>
                {clients.filter((c) => c.status !== 'archived').map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('Name')}</label>
            <input
              className="form-input"
              value={form.name}
              placeholder={t('Warehouse slotting optimisation')}
              onChange={(ev) => setForm({ ...form, name: ev.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('Code — uppercase alnum, unique, immutable')}</label>
            <input
              className="form-input mono"
              value={form.code}
              placeholder={t('leave blank to derive from the client')}
              onChange={(ev) => setForm({ ...form, code: ev.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
            />
          </div>

          <div className="flex gap-12">
            <div className="form-group flex-1">
              <label className="form-label">{t('Sponsor')}</label>
              <select
                className="form-select"
                value={form.sponsor_contact_id}
                onChange={(ev) => setForm({ ...form, sponsor_contact_id: ev.target.value })}
                disabled={!chosenClient}
              >
                <option value="">
                  {!chosenClient ? t('pick a client first') : contacts.length ? t('not named yet') : t('no contacts on this client')}
                </option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.role_title ? ` · ${c.role_title}` : ''}</option>
                ))}
              </select>
              <div className="text-xs text-ink-3 mt-4">
                {tx(t, 'Who signs. Naming them here clears the counterpart condition on gate {gate}.', { gate: <span className="mono">G0</span> })}
              </div>
            </div>
            <div className="form-group" style={{ width: 170 }}>
              <label className="form-label">{t('Estimate ({currency})', { currency })}</label>
              <input
                className="form-input mono"
                type="number"
                min={0}
                step="1000"
                placeholder="45000"
                value={form.estimate_value}
                onChange={(ev) => setForm({ ...form, estimate_value: ev.target.value })}
              />
              <div className="text-xs text-ink-3 mt-4">{t('Ballpark, before milestones exist.')}</div>
            </div>
          </div>

          <div className="flex gap-12">
            <div className="form-group flex-1">
              <label className="form-label">{t('Commercial model')}</label>
              <select
                className="form-select"
                value={form.commercial_model}
                onChange={(ev) => setForm({ ...form, commercial_model: ev.target.value })}
              >
                {Object.entries(crm.COMMERCIAL_MODEL_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{t(v)}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ width: 110 }}>
              <label className="form-label">{t('Currency')}</label>
              <input
                className="form-input mono"
                value={form.currency}
                maxLength={3}
                onChange={(ev) => setForm({ ...form, currency: ev.target.value.toUpperCase() })}
              />
            </div>
            <div className="form-group" style={{ width: 130 }}>
              <label className="form-label">{t('Review window (d)')}</label>
              <input
                className="form-input mono"
                type="number"
                min={0}
                max={365}
                value={form.review_window_days}
                onChange={(ev) => setForm({ ...form, review_window_days: ev.target.value })}
              />
            </div>
          </div>

          <div className="flex gap-12">
            <div className="form-group flex-1">
              <label className="form-label">{t('Correspondence language')}</label>
              <select
                className="form-select"
                value={form.language}
                onChange={(ev) => setForm({ ...form, language: ev.target.value })}
              >
                <option value="">
                  {(() => {
                    const c = clients.find((x) => x.id === chosenClient);
                    const l = c && isLanguage(c.language) ? c.language : null;
                    return l ? t('inherit from the client — {language}', { language: LANGUAGE_LABEL[l] }) : t('inherit from the client');
                  })()}
                </option>
                {LANGUAGES.map((l) => <option key={l} value={l}>{LANGUAGE_LABEL[l]}</option>)}
              </select>
              <div className="form-hint">{t('Everything produced inside the engagement — proposals, descriptors, questions — is written in it.')}</div>
            </div>
          </div>

          <div className="flex gap-12">
            <div className="form-group flex-1">
              <label className="form-label">{t('Parent engagement (optional)')}</label>
              <select
                className="form-select"
                value={form.parent_engagement_id}
                onChange={(ev) => setForm({ ...form, parent_engagement_id: ev.target.value })}
              >
                <option value="">{t('none')}</option>
                {engagements
                  .filter((e) => !chosenClient || e.client_id === chosenClient)
                  .map((e) => <option key={e.id} value={e.id}>{e.code} · {e.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ width: 170 }}>
              <label className="form-label">{t('Kickoff date (optional)')}</label>
              <input
                className="form-input mono"
                type="date"
                value={form.kickoff_date}
                onChange={(ev) => setForm({ ...form, kickoff_date: ev.target.value })}
              />
            </div>
          </div>

          <div className="text-xs text-ink-3" style={{ lineHeight: 1.5 }}>
            {tx(t, 'The engagement opens in {phase} behind gate {gate}. Its code is used in refs and the agent workspace directory and cannot be changed later.', {
              phase: <strong>{t(crm.PHASE_LABELS.qualify)}</strong>,
              gate: <span className="mono">G0</span>,
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn btn-accent" disabled={saving} onClick={() => void create()}>
            {saving ? t('Creating…') : t('Create engagement')}
          </button>
        </div>
      </div>
    </div>
  );
}
